import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCompany } from '@/contexts/CompanyContext';

export function PDFWizardPage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<'blank' | 'formal' | 'simple' | 'detailed'>('formal');

  const handleBack = () => {
    navigate('/settings/templates');
  };

  const handleContinue = () => {
    // Navigate to builder with template details
    navigate('/settings/templates/builder/new', {
      state: {
        templateName,
        templateType,
        description,
      }
    });
  };

  const canProceed = () => {
    return templateName.trim().length > 0 && !!selectedCompany?.id;
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div>
                <h1 className="text-2xl font-semibold">Create Statement Template</h1>
                <p className="text-sm text-muted-foreground">
                  Configure your template details
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
              <CardDescription>
                Provide basic information about your PDF template
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="template-name">
                    Template Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    placeholder="e.g., Monthly Statement, Invoice Summary"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    autoFocus
                  />
                  <p className="text-sm text-muted-foreground">
                    Choose a descriptive name that helps identify this template. Text, maximum 200 characters.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description of when to use this template"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Company</Label>
                  {selectedCompany ? (
                    <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/50">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium">{selectedCompany.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        No company selected. Please select a company from the sidebar.
                      </p>
                    </div>
                  )}
                  
                  {/* Company Logo Status */}
                  {selectedCompany?.logoUrl && (
                    <div className="flex items-start gap-3 p-3 rounded-md bg-primary/10 border border-primary/20">
                      <img 
                        src={selectedCompany.logoUrl} 
                        alt={`${selectedCompany.name} logo`}
                        className="h-10 object-contain"
                      />
                      <div className="flex items-start gap-2 flex-1">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-primary">
                          Logo found — will be applied to your template
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {selectedCompany && !selectedCompany.logoUrl && (
                    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        No logo found for {selectedCompany.name}. Your template will be created without a logo.
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-4">
                  <div>
                    <Label>Starting Point</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Choose how you want to start building your template
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Start from Blank */}
                    <button
                      type="button"
                      onClick={() => setTemplateType('blank')}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                        templateType === 'blank' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <div className="font-medium mb-1">Start from Blank</div>
                      <div className="text-sm text-muted-foreground">
                        No pre-set sections. All sections start turned off — you choose exactly what to include
                      </div>
                    </button>

                    {/* Formal Template */}
                    <button
                      type="button"
                      onClick={() => setTemplateType('formal')}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                        templateType === 'formal' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <div className="font-medium mb-1">Formal Template</div>
                      <div className="text-sm text-muted-foreground">
                        Header with logo and company details, bordered transaction table, totals block, payment instructions footer
                      </div>
                    </button>

                    {/* Simple Template */}
                    <button
                      type="button"
                      onClick={() => setTemplateType('simple')}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                        templateType === 'simple' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <div className="font-medium mb-1">Simple Template</div>
                      <div className="text-sm text-muted-foreground">
                        Minimal header, clean borderless transaction table, outstanding balance total
                      </div>
                    </button>

                    {/* Detailed Template */}
                    <button
                      type="button"
                      onClick={() => setTemplateType('detailed')}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                        templateType === 'detailed' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <div className="font-medium mb-1">Detailed Template</div>
                      <div className="text-sm text-muted-foreground">
                        Full header, account summary panel, transaction table, aging breakdown by period, totals, footer
                      </div>
                    </button>
                  </div>
                </div>
                
              {/* Continue Button */}
              <div className="flex justify-end pt-6 border-t">
                <Button
                  onClick={handleContinue}
                  disabled={!canProceed()}
                  className="gap-2"
                >
                  Continue to Builder
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
